/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable max-lines */
import {
  BottomSheet,
  Text,
  Title,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { isDefined, toError } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { CommonDialog } from "@src/appV2/lib/Dialogs/Common/Dialog";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { extractPhoneNumberDigits } from "@src/appV2/lib/utils/formatPhoneNumber";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { useUpdateProfile } from "@src/appV2/redesign/Profile/api/useUpdateProfile";
import {
  createPhoneFormSchema,
  normalizeTo10Digits,
} from "@src/appV2/redesign/Profile/types/phoneFormSchema";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { type z } from "zod";

import {
  type PlacementCandidate,
  PlacementCandidateScreeningStatus,
} from "../../../PlacementCandidate/types";
import { type BookingOrderingCohort } from "../../hooks/useBookingOrderingCohort";
import { useNotifyRecruiterForScreening } from "../../hooks/useNotifyRecruiterForScreening";
import { usePlacementRecruiterScreeningConfig } from "../../hooks/usePlacementRecruiterScreeningConfig";
import { PLACEMENT_PATHS } from "../../paths";
import { InitialStepContent } from "./InitialStepContent";
import { PhoneConfirmationStepContent } from "./PhoneConfirmationStepContent";
import { SuccessStepContent } from "./SuccessStepContent";

interface RecruiterScreeningCallDialogProps {
  modalState: UseModalState;
  placementId?: string;
  source?: "banner" | "detail-cta";
  placementCandidate?: PlacementCandidate;
  bookingReorderingCohort: BookingOrderingCohort;
  selectedInterviewTime?: { start: Date; end: Date };
}

type ScreeningStep = "initial" | "phone-confirmation" | "success";

type PhoneFormData = z.infer<ReturnType<typeof createPhoneFormSchema>>;

export function RecruiterScreeningCallDialog(props: RecruiterScreeningCallDialogProps) {
  const {
    modalState,
    placementId,
    source = "detail-cta",
    placementCandidate,
    bookingReorderingCohort,
    selectedInterviewTime,
  } = props;
  const { screeningCalendlyLink, showCallMeNow } = usePlacementRecruiterScreeningConfig();
  const exitConfirmationModalState = useModalState();
  const worker = useDefinedWorker();
  const history = useHistory();
  const { showErrorToast } = useToast();

  const [step, setStep] = useState<ScreeningStep>("initial");

  // Create schema with existing phone to skip existence check when phone hasn't changed
  const phoneSchema = useMemo(() => createPhoneFormSchema(worker.phone), [worker.phone]);

  const phoneForm = useForm<PhoneFormData>({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      phone: worker.phone ?? "",
    },
  });

  const { mutateAsync: updateProfile } = useUpdateProfile();

  const isPendingScreening =
    placementCandidate?.screeningStatus === PlacementCandidateScreeningStatus.PENDING;

  const { mutateAsync: notifyRecruiterForScreening, isLoading: isNotifyingRecruiter } =
    useNotifyRecruiterForScreening({
      onSuccess: () => {
        setStep("success");
      },
      onError: (error: unknown) => {
        logError(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_NOTIFY_RECRUITER_FAILED, {
          error,
          metadata: {
            placementId,
            source,
            bookingReorderingCohort,
          },
        });
        showErrorToast("Failed to request call. Please try again.");
      },
    });

  const isScreenFirst = bookingReorderingCohort === "screen_first";

  const handleClose = () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_EXIT_CLICKED, {
      source,
      ...(isDefined(placementId) && { placementId }),
      bookingReorderingCohort,
    });
    exitConfirmationModalState.openModal();
  };

  return (
    <>
      <BottomSheet
        modalState={modalState}
        canDismiss={false}
        header={<BottomSheetHeader onClose={handleClose} />}
        onClose={handleClose}
      >
        <Box sx={{ px: 5, py: 6 }}>
          {step === "initial" && (
            <InitialStepContent
              isPendingScreening={isPendingScreening}
              isNotifyingRecruiter={isNotifyingRecruiter}
              screeningCalendlyLink={screeningCalendlyLink}
              isScreenFirst={isScreenFirst}
              showCallMeNow={showCallMeNow}
              source={source}
              onCallMeNow={() => {
                logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_CALL_ME_NOW_CLICKED, {
                  source,
                  ...(isDefined(placementId) && { placementId }),
                  bookingReorderingCohort,
                });
                setStep("phone-confirmation");
              }}
              onScheduleLater={async () => {
                if (!screeningCalendlyLink) {
                  logError(
                    APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_SCHEDULE_LATER_MISSING_LINK,
                    {
                      error: "screeningCalendlyLink is missing",
                      metadata: {
                        placementId,
                        source,
                        bookingReorderingCohort,
                      },
                    }
                  );
                  showErrorToast("Unable to schedule call. Please try again later.");
                  return;
                }

                logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_SCHEDULE_LATER_CLICKED, {
                  source,
                  ...(isDefined(placementId) && { placementId }),
                  bookingReorderingCohort,
                });

                try {
                  await openInBrowser(
                    { url: screeningCalendlyLink },
                    "recruiter-screening-schedule-later"
                  );
                } catch (maybeError: unknown) {
                  logError(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_OPEN_BROWSER_FAILED, {
                    error: toError(maybeError),
                    metadata: {
                      placementId,
                      source,
                      bookingReorderingCohort,
                      url: screeningCalendlyLink,
                    },
                  });
                  showErrorToast("Unable to open scheduling link. Please try again.");
                  return;
                }

                setStep("initial");
                modalState.closeModal();
              }}
            />
          )}

          {step === "phone-confirmation" && (
            <FormProvider {...phoneForm}>
              <PhoneConfirmationStepContent
                isNotifyingRecruiter={isNotifyingRecruiter}
                isScreenFirst={isScreenFirst}
                source={source}
                onConfirm={async (data: PhoneFormData) => {
                  const sanitizedPhone = extractPhoneNumberDigits(data.phone);
                  const normalizedNewPhone = normalizeTo10Digits(sanitizedPhone);
                  const normalizedExistingPhone = normalizeTo10Digits(
                    extractPhoneNumberDigits(worker.phone ?? "")
                  );

                  logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_PHONE_CONFIRMED, {
                    source,
                    ...(isDefined(placementId) && { placementId }),
                    bookingReorderingCohort,
                  });

                  if (normalizedNewPhone !== normalizedExistingPhone) {
                    try {
                      await updateProfile({ profile: { phone: sanitizedPhone } });
                    } catch (maybeError: unknown) {
                      logError(APP_V2_APP_EVENTS.RECRUITER_SCREENING_PHONE_UPDATE_FAILED, {
                        error: toError(maybeError),
                        metadata: {
                          placementId,
                          workerId: worker.userId,
                        },
                      });
                    }
                  }

                  try {
                    await notifyRecruiterForScreening({
                      source,
                      contactPhone: sanitizedPhone,
                      ...(isDefined(placementId) && { placementId }),
                      bookingReorderingCohort,
                      ...(isDefined(selectedInterviewTime) && {
                        interviewTime: selectedInterviewTime,
                      }),
                    });
                  } catch {
                    // Error handled by onError callback in mutation hook
                  }
                }}
                onBack={() => {
                  logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_BACK_CLICKED, {
                    source,
                    ...(isDefined(placementId) && { placementId }),
                    bookingReorderingCohort,
                  });
                  setStep("initial");
                }}
              />
            </FormProvider>
          )}

          {step === "success" && (
            <SuccessStepContent
              onBrowseMoreJobs={() => {
                logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_BROWSE_JOBS_CLICKED, {
                  source,
                  ...(isDefined(placementId) && { placementId }),
                  bookingReorderingCohort,
                });
                setStep("initial");
                modalState.closeModal();
                history.replace(PLACEMENT_PATHS.ROOT);
              }}
            />
          )}
        </Box>
      </BottomSheet>

      <CommonDialog
        modalState={exitConfirmationModalState}
        title={
          <Title bold component="h3" textAlign="center">
            Are you sure you want to exit?
          </Title>
        }
        actions={
          <Stack width="100%" spacing={1}>
            <Button
              variant="contained"
              onClick={() => {
                logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_EXIT_DIALOG_CONFIRMED, {
                  source,
                  ...(isDefined(placementId) && { placementId }),
                  bookingReorderingCohort,
                });
                setStep("initial");
                exitConfirmationModalState.closeModal();
                modalState.closeModal();
              }}
            >
              Yes, exit
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_EXIT_DIALOG_CANCELLED, {
                  source,
                  ...(isDefined(placementId) && { placementId }),
                  bookingReorderingCohort,
                });
                exitConfirmationModalState.closeModal();
              }}
            >
              Cancel
            </Button>
          </Stack>
        }
      >
        <Text textAlign="center">
          You&apos;ll need to complete your brief screening call with Clipboard in order to book an
          instant interview.
        </Text>
      </CommonDialog>
    </>
  );
}
